import { id } from "@instantdb/react";
import { createFileRoute } from "@tanstack/react-router";
import { CalendarDays, Clock3, MessageCircle, Play, Send } from "lucide-react";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import { db } from "@/lib/instantdb";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";
import {
	WEBINAR_DATE_LABEL,
	WEBINAR_DURATION_LABEL,
	WEBINAR_START_ISO,
	WEBINAR_TIME_LABEL,
	WEBINAR_TITLE,
} from "@/lib/webinar-constants";

const description =
	"The live room for AgentReacher's webinar on using ChatGPT or Claude to execute social media workflows.";
const WEBINAR_CHAT_SLUG = "boost-your-socials-2026-07-01";
const WEBINAR_CHAT_NAME_KEY = "agentreacher-webinar-chat-name";
const WEBINAR_CHAT_VISITOR_KEY = "agentreacher-webinar-chat-visitor-id";

export const Route = createFileRoute("/webinar/live")({
	component: WebinarLivePage,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({
			matches,
			internalPath: "/webinar/live",
		});

		return {
			links: buildLocalizedSeoLinks({
				matches,
				internalPath: "/webinar/live",
			}),
			meta: [
				{ title: `Webinar room: ${WEBINAR_TITLE}` },
				{ name: "description", content: description },
				{ property: "og:title", content: `Webinar room: ${WEBINAR_TITLE}` },
				{ property: "og:description", content: description },
				{ property: "og:image", content: ogImageUrl("webinar") },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ name: "robots", content: "noindex" },
			],
		};
	},
});

function WebinarLivePage() {
	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground">
			<PublicHeader />
			<main className="relative z-10 flex-1">
				<div className="mx-auto max-w-6xl px-4 sm:px-6">
					<div className="marketing-line-stack flex flex-col overflow-hidden divide-y divide-border">
						<section className="grid gap-px bg-border lg:grid-cols-[1fr_20rem]">
							<div className="homepage-hover-cell rounded-md bg-white px-5 py-10 sm:px-10 lg:px-12 dark:bg-black">
								<p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
									Webinar room
								</p>
								<h1 className="mt-3 max-w-3xl text-4xl font-semibold leading-[1.02] tracking-[-0.05em] sm:text-5xl">
									Boost Your Socials
								</h1>
								<p className="mt-5 max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
									The live stream will appear here before the session starts.
									Keep this page handy from your calendar invite.
								</p>

								<div className="mt-8 aspect-video overflow-hidden rounded-md border border-border bg-muted/40">
									<div
										className="flex h-full flex-col justify-between p-5 sm:p-6"
										style={{
											backgroundImage:
												"radial-gradient(rgba(17,24,39,0.05) 1px, transparent 1px), radial-gradient(ellipse 90% 80% at 50% 45%, rgba(17,24,39,0.08) 0%, rgba(17,24,39,0.035) 38%, transparent 72%)",
											backgroundSize: "22px 22px, 100% 100%",
										}}
									>
										<div className="flex items-center justify-between gap-4">
											<div className="flex items-center gap-2">
												<span className="size-2 rounded-full bg-muted-foreground/40" />
												<span className="size-2 rounded-full bg-muted-foreground/30" />
												<span className="size-2 rounded-full bg-muted-foreground/20" />
											</div>
											<div className="flex flex-wrap items-center justify-end gap-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
												<Countdown variant="inline" />
											</div>
										</div>

										<div className="flex flex-col items-center justify-center text-center">
											<span className="flex size-14 items-center justify-center rounded-full border border-border bg-background/70 shadow-sm backdrop-blur">
												<Play className="ml-0.5 size-6 text-foreground" />
											</span>
											<p className="mt-4 text-sm font-semibold">
												Live stream coming soon
											</p>
											<p className="mt-1 max-w-sm text-sm leading-6 text-muted-foreground">
												The webinar embed or live meeting link will be placed in
												this area.
											</p>
										</div>

										<div className="grid gap-2 sm:grid-cols-3">
											<div className="h-2 rounded-full bg-foreground/10" />
											<div className="h-2 rounded-full bg-foreground/10 sm:col-span-2" />
										</div>
									</div>
								</div>
							</div>

							<aside className="homepage-hover-cell flex flex-col rounded-md bg-white p-5 sm:p-6 dark:bg-black">
								<p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
									Session details
								</p>
								<div className="mt-5 space-y-5 text-sm">
									<LiveRoomFact
										icon={CalendarDays}
										label="Date"
										value={WEBINAR_DATE_LABEL}
									/>
									<LiveRoomFact
										icon={Clock3}
										label="Time"
										value={WEBINAR_TIME_LABEL}
									/>
									<LiveRoomFact
										icon={MessageCircle}
										label="Format"
										value={`Live demo + Q&A, ${WEBINAR_DURATION_LABEL}`}
									/>
								</div>
								<LiveChat />
							</aside>
						</section>
					</div>
				</div>
			</main>
			<PublicFooter />
		</div>
	);
}

function LiveChat() {
	const [authorName, setAuthorName] = useState("");
	const [hasSavedName, setHasSavedName] = useState(false);
	const [visitorId, setVisitorId] = useState("");
	const [message, setMessage] = useState("");
	const [isSending, setIsSending] = useState(false);

	useEffect(() => {
		const storedName = window.localStorage.getItem(WEBINAR_CHAT_NAME_KEY);
		const storedVisitorId = window.localStorage.getItem(
			WEBINAR_CHAT_VISITOR_KEY,
		);
		const nextVisitorId = storedVisitorId || crypto.randomUUID();

		if (!storedVisitorId) {
			window.localStorage.setItem(WEBINAR_CHAT_VISITOR_KEY, nextVisitorId);
		}

		setAuthorName(storedName ?? "");
		setHasSavedName(Boolean(storedName));
		setVisitorId(nextVisitorId);
	}, []);

	const { data, isLoading } = db.useQuery({
		webinarChatMessages: {
			$: {
				where: { webinarSlug: WEBINAR_CHAT_SLUG },
				order: { createdAt: "asc" },
				limit: 100,
			},
		},
	});

	const messages = useMemo(() => {
		return (data?.webinarChatMessages ?? []).toSorted(
			(a, b) =>
				new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
		);
	}, [data?.webinarChatMessages]);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const trimmedName = authorName.trim();
		const trimmedMessage = message.trim();
		if (!trimmedName || !trimmedMessage || isSending) return;

		const nextVisitorId = visitorId || crypto.randomUUID();
		setIsSending(true);

		try {
			window.localStorage.setItem(WEBINAR_CHAT_NAME_KEY, trimmedName);
			window.localStorage.setItem(WEBINAR_CHAT_VISITOR_KEY, nextVisitorId);
			setHasSavedName(true);
			setVisitorId(nextVisitorId);

			await db.transact(
				db.tx.webinarChatMessages[id()].update({
					authorName: trimmedName,
					createdAt: new Date(),
					message: trimmedMessage,
					visitorId: nextVisitorId,
					webinarSlug: WEBINAR_CHAT_SLUG,
				}),
			);
			setMessage("");
		} finally {
			setIsSending(false);
		}
	}

	return (
		<div className="mt-8 flex min-h-[22rem] flex-1 flex-col border-t border-border pt-6">
			<div className="flex items-center justify-between gap-3">
				<div>
					<p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
						Live chat
					</p>
				</div>
				<span className="rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground">
					{messages.length}
				</span>
			</div>

			<div className="mt-4 flex-1 space-y-3 overflow-y-auto rounded-md border border-border bg-muted/20 p-3">
				{isLoading ? (
					<p className="text-sm text-muted-foreground">Loading chat...</p>
				) : messages.length === 0 ? (
					<p className="text-sm text-muted-foreground">
						No messages yet. Be the first to join in.
					</p>
				) : (
					messages.map((chatMessage) => (
						<div key={chatMessage.id} className="rounded-md bg-background p-3">
							<div className="flex items-baseline justify-between gap-3">
								<p className="text-sm font-semibold">
									{chatMessage.authorName}
								</p>
								<p className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
									{formatChatTime(chatMessage.createdAt)}
								</p>
							</div>
							<p className="mt-1 break-words text-sm leading-6 text-muted-foreground">
								{chatMessage.message}
							</p>
						</div>
					))
				)}
			</div>

			<form className="mt-4 space-y-3" onSubmit={handleSubmit}>
				{hasSavedName ? (
					<p className="text-xs text-muted-foreground">
						Chatting as{" "}
						<span className="font-medium text-foreground">
							{authorName.trim()}
						</span>
					</p>
				) : (
					<label className="block">
						<span className="text-sm font-medium">Your name</span>
						<input
							value={authorName}
							onChange={(event) => setAuthorName(event.target.value)}
							placeholder="Ada"
							maxLength={48}
							className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-foreground"
						/>
					</label>
				)}
				<div className="flex gap-2">
					<input
						value={message}
						onChange={(event) => setMessage(event.target.value)}
						placeholder="Type a message..."
						maxLength={500}
						className="h-10 min-w-0 flex-1 rounded-md border border-input bg-background px-3 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-foreground"
					/>
					<button
						type="submit"
						disabled={!authorName.trim() || !message.trim() || isSending}
						className="inline-flex size-10 shrink-0 items-center justify-center rounded-md bg-foreground text-background transition-colors hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-50"
						aria-label="Send chat message"
					>
						<Send className="size-4" />
					</button>
				</div>
			</form>
		</div>
	);
}

function formatChatTime(value: Date | string | number) {
	return new Intl.DateTimeFormat("en", {
		hour: "2-digit",
		minute: "2-digit",
	}).format(new Date(value));
}

function Countdown({
	className = "",
	variant = "card",
}: {
	className?: string;
	variant?: "card" | "inline";
}) {
	const [remaining, setRemaining] = useState<{
		days: number;
		hours: number;
		minutes: number;
		seconds: number;
	} | null>(null);

	useEffect(() => {
		const target = new Date(WEBINAR_START_ISO).getTime();

		function update() {
			const diff = Math.max(0, target - Date.now());
			const days = Math.floor(diff / 86_400_000);
			const hours = Math.floor((diff % 86_400_000) / 3_600_000);
			const minutes = Math.floor((diff % 3_600_000) / 60_000);
			const seconds = Math.floor((diff % 60_000) / 1000);
			setRemaining({ days, hours, minutes, seconds });
		}

		update();
		const interval = window.setInterval(update, 1000);
		return () => window.clearInterval(interval);
	}, []);

	const parts = remaining ?? { days: 0, hours: 0, minutes: 0, seconds: 0 };

	if (variant === "inline") {
		return (
			<div
				className={`inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground ${className}`}
			>
				<span>Starts in</span>
				<span className="tabular-nums">{parts.days}d</span>
				<span className="tabular-nums">
					{String(parts.hours).padStart(2, "0")}h
				</span>
				<span className="tabular-nums">
					{String(parts.minutes).padStart(2, "0")}m
				</span>
				<span className="tabular-nums">
					{String(parts.seconds).padStart(2, "0")}s
				</span>
			</div>
		);
	}

	return (
		<div
			className={`rounded-md border border-border bg-muted/40 p-4 ${className}`}
		>
			<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
				Starts in
			</p>
			<div className="mt-3 grid grid-cols-4 gap-2 text-center">
				<CountdownPart label="Days" value={parts.days} />
				<CountdownPart label="Hours" value={parts.hours} />
				<CountdownPart label="Min" value={parts.minutes} />
				<CountdownPart label="Sec" value={parts.seconds} />
			</div>
		</div>
	);
}

function CountdownPart({ label, value }: { label: string; value: number }) {
	return (
		<div>
			<p className="text-lg font-semibold tabular-nums leading-none">
				{String(value).padStart(2, "0")}
			</p>
			<p className="mt-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
				{label}
			</p>
		</div>
	);
}

function LiveRoomFact({
	icon: Icon,
	label,
	value,
}: {
	icon: typeof CalendarDays;
	label: string;
	value: string;
}) {
	return (
		<div className="flex gap-3">
			<span className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-muted/40">
				<Icon className="size-4 text-foreground" />
			</span>
			<div>
				<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
					{label}
				</p>
				<p className="mt-1 font-semibold leading-6">{value}</p>
			</div>
		</div>
	);
}
